// ============================================
// MoltiVerse v3 - API Hooks with TanStack Query
// ============================================
// Centralized data fetching with caching, deduplication, and auto-revalidation

import { useQuery, useMutation, useQueryClient, QueryClient } from '@tanstack/react-query';
import type { Agent, Connection, UniverseStats } from './types';

// ============ QUERY CLIENT CONFIGURATION ============
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000, // 1 minute before considered stale
      gcTime: 300_000, // 5 minutes cache time (previously cacheTime)
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false, // Disable refetch on window focus for real-time app
    },
  },
});

// ============ API BASE ============
const API_BASE = '/api';

// ============ SAFE FETCH WRAPPER ============
async function safeFetch<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return data as T;
}

// ============ TYPE DEFINITIONS ============
interface ProgressionData {
  agentGrowth: Array<{ time_bucket: string; new_agents: string; cumulative_agents: string }>;
  postGrowth: Array<{ time_bucket: string; new_posts: string; cumulative_posts: string }>;
  interactionGrowth: Array<{ time_bucket: string; new_interactions: string; cumulative_interactions: string }>;
  granularity: string;
}

interface AgentProgressionData {
  karmaHistory: Array<{ agent_id: string; time_bucket: string; karma: string; karma_delta: string }>;
  postActivity: Array<{ agent_id: string; time_bucket: string; posts: string; karma_earned: string }>;
  agents: Agent[];
}

interface AgentNetworkData {
  influencers: Array<Agent & { interaction_weight: number; interaction_count: number }>;
  influenced: Array<Agent & { interaction_weight: number; interaction_count: number }>;
  connections: Connection[];
}

interface AgentPostsData {
  posts: Array<{
    id: string;
    title: string;
    content: string;
    submolt: string;
    upvotes: number;
    downvotes: number;
    created_at: string;
  }>;
  total: number;
}

// ============ QUERY KEYS ============
export const queryKeys = {
  stats: ['stats'] as const,
  agents: ['agents'] as const,
  agent: (id: string) => ['agent', id] as const,
  agentNetwork: (id: string) => ['agent', id, 'network'] as const,
  agentPosts: (id: string, page?: number) => ['agent', id, 'posts', page] as const,
  progression: (granularity: string) => ['progression', granularity] as const,
  agentProgression: (ids: string[], granularity: string) => ['agentProgression', ids, granularity] as const,
  search: (query: string) => ['search', query] as const,
};

// ============ HOOKS ============

/**
 * Fetch universe statistics
 */
export function useUniverseStats() {
  return useQuery({
    queryKey: queryKeys.stats,
    queryFn: () => safeFetch<UniverseStats>(`${API_BASE}/stats`),
    staleTime: 30_000, // Stats are updated frequently
    refetchInterval: 60_000, // Auto-refresh every minute
  });
}

/**
 * Fetch a single agent by ID
 */
export function useAgent(id: string | null) {
  return useQuery({
    queryKey: queryKeys.agent(id || ''),
    queryFn: () => safeFetch<Agent>(`${API_BASE}/agents/${id}`),
    enabled: !!id,
    staleTime: 120_000, // Agent data changes less frequently
  });
}

/**
 * Fetch agent network (influencers + influenced)
 */
export function useAgentNetwork(id: string | null) {
  return useQuery({
    queryKey: queryKeys.agentNetwork(id || ''),
    queryFn: () => safeFetch<AgentNetworkData>(`${API_BASE}/agents/${id}/network`),
    enabled: !!id,
    staleTime: 120_000,
  });
}

/**
 * Fetch agent's posts
 */
export function useAgentPosts(id: string | null, page = 1) {
  return useQuery({
    queryKey: queryKeys.agentPosts(id || '', page),
    queryFn: () => safeFetch<AgentPostsData>(`${API_BASE}/agents/${id}/posts?page=${page}&limit=20`),
    enabled: !!id,
    staleTime: 60_000,
  });
}

/**
 * Fetch network progression analytics
 */
export function useNetworkProgression(granularity: 'day' | 'week' = 'day') {
  return useQuery({
    queryKey: queryKeys.progression(granularity),
    queryFn: () => safeFetch<ProgressionData>(`${API_BASE}/analytics/progression?granularity=${granularity}`),
    staleTime: 300_000, // Analytics data is relatively stable
  });
}

/**
 * Fetch agent progression comparison
 */
export function useAgentProgression(agentIds: string[], granularity: 'day' | 'week' = 'day') {
  return useQuery({
    queryKey: queryKeys.agentProgression(agentIds, granularity),
    queryFn: () => safeFetch<AgentProgressionData>(
      `${API_BASE}/analytics/agents?agents=${agentIds.join(',')}&granularity=${granularity}`
    ),
    enabled: agentIds.length > 0,
    staleTime: 300_000,
  });
}

/**
 * Search agents
 */
export function useSearchAgents(query: string) {
  return useQuery({
    queryKey: queryKeys.search(query),
    queryFn: () => safeFetch<{ results: Agent[] }>(`${API_BASE}/search?q=${encodeURIComponent(query)}`),
    enabled: query.length >= 2,
    staleTime: 60_000,
  });
}

// ============ PREFETCH HELPERS ============

/**
 * Prefetch agent data for hover/preview
 */
export function prefetchAgent(id: string) {
  queryClient.prefetchQuery({
    queryKey: queryKeys.agent(id),
    queryFn: () => safeFetch<Agent>(`${API_BASE}/agents/${id}`),
    staleTime: 120_000,
  });
}

/**
 * Prefetch agent network for anticipatory loading
 */
export function prefetchAgentNetwork(id: string) {
  queryClient.prefetchQuery({
    queryKey: queryKeys.agentNetwork(id),
    queryFn: () => safeFetch<AgentNetworkData>(`${API_BASE}/agents/${id}/network`),
    staleTime: 120_000,
  });
}

// ============ MUTATION HOOKS ============

/**
 * Refresh all cached data
 */
export function useRefreshAll() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      await queryClient.invalidateQueries();
      return true;
    },
  });
}

// ============ CACHE UTILITIES ============

/**
 * Get cached agent data (synchronous)
 */
export function getCachedAgent(id: string): Agent | undefined {
  return queryClient.getQueryData<Agent>(queryKeys.agent(id));
}

/**
 * Set agent data in cache
 */
export function setCachedAgent(agent: Agent) {
  queryClient.setQueryData(queryKeys.agent(agent.id), agent);
}

/**
 * Invalidate agent cache
 */
export function invalidateAgentCache(id: string) {
  queryClient.invalidateQueries({ queryKey: queryKeys.agent(id) });
}
